import { useSnackbar } from "notistack"

import * as storageApi from "../api"
import { unknownErrorToString } from "../../utils/errors"
import { useTranslation } from "react-i18next"

export const useFileUpload = () => {
  const translations = useTranslations()
  const { enqueueSnackbar } = useSnackbar()

  const handleError = (error: any) => {
    const message = unknownErrorToString(error)
    enqueueSnackbar(message, { variant: "error" })
  }

  const [generateUploadUrls] = storageApi.useGeneratePresignedUploadUrlsMutation()

  const [confirmUpload] = storageApi.useConfirmUploadMutation()

  const [attachFiles] = storageApi.useAttachFilesMutation()

  const uploadFiles = async ({ files, entityId, entityType, orgId }: UploadFilesParams) => {
    try {
      if (!files.length) return false

      const presigned = await generateUploadUrls({
        files: files.map((f) => ({
          filename: f.name,
          contentType: f.type || "application/octet-stream", // Default to binary stream if type is missing
        })),
        orgId,
      }).unwrap()

      const keys = await Promise.all(
        presigned.files.map(async (p, index) => {
          const file = files[index]

          await fetch(p.uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": file.type,
            },
            body: file,
          })

          await confirmUpload({ key: p.key }).unwrap()

          return p.key
        })
      )

      await attachFiles({
        keys,
        entityId,
        entityType,
      }).unwrap()

      enqueueSnackbar(translations.uploadSuccess, {
        variant: "success",
      })

      return true
    } catch (err) {
      handleError(err)

      enqueueSnackbar(translations.uploadFailed, {
        variant: "error",
      })

      return false
    }
  }

  return {
    uploadFiles,
  }
}

type UploadFilesParams = {
  files: File[]
  entityId: string
  entityType: "project" | "task"
  orgId?: string
}

const defaultTranslations = {
  uploadSuccess: "Files uploaded successfully",
  uploadFailed: "Failed to upload file",
}

const useTranslations = (defaults = defaultTranslations) => {
  const { t } = useTranslation("storage-file")

  return {
    uploadSuccess: t("fileUploadSuccess", defaults.uploadSuccess),
    uploadFailed: t("fileUploadError", defaults.uploadFailed),
  }
}
